import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Box, Button, Container, Grid, Typography } from "@mui/material"
import { styled } from "@mui/material/styles"

import { faCheckCircle, faTimesCircle } from "@fortawesome/free-solid-svg-icons"
import PaperBox from "components/PaperBox"
import TodoListCheckbox from "components/dataDisplay/TodoList/TodoListCheckbox"
import TodoListRow from "components/dataDisplay/TodoList/TodoListRow"
import { TodoListWrapper } from "components/dataDisplay/TodoList/TodoListWrapper"
import theme from "css/theme/light"
import { allRoutes } from "generated/routes"
import { useTranslation } from "react-i18next"
import { NavLink } from "react-router-dom"

const PassedExam = ({ item }) => {
  const { t } = useTranslation()
  return (
    <Grid spacing={2} container direction="column" justifyContent="center" alignItems="center">
      <Grid item>
        <FontAwesomeIcon color={theme.palette.success["main"]} icon={faCheckCircle} size="3x" />
      </Grid>
      <Grid item>
        <Typography variant="h1" marginBottom={2} align="center">
          {t("learning.result.titles.passedTest", "Congratulations! You passed the test!")}
        </Typography>
        <Typography variant="bodyMedium" align="center" color="textSecondary">
          {t("learning.result.titles.youHad", "You had")} {item.correctAnswersCount}{" "}
          {t("learning.result.titles.correct", "answers")} {t("learning.result.titles.of", "of")}{" "}
          {item.totalQuestionsCount} {t("learning.result.titles.possible", "possible")}.{" "}
          {t("learning.result.titles.approvedLimit", "The limit for approved is")} {item.requiredPassingScore}
          .
        </Typography>
      </Grid>
    </Grid>
  )
}
const NotPassedExam = ({ item }) => {
  const { t } = useTranslation()
  return (
    <Grid spacing={2} container direction="column" justifyContent="center" alignItems="center">
      <Grid item>
        <FontAwesomeIcon color={theme.palette.error["main"]} icon={faTimesCircle} size="3x" />
      </Grid>
      <Grid item>
        <Typography variant="h1" marginBottom={2} align="center">
          {t("learning.result.titles.failedTest", "Unfortunately, your result has not been approved")}
        </Typography>
        <Typography variant="bodyMedium" align="center" color="textSecondary">
          {t("learning.result.titles.redoTest", "You need to retake the exam to be approved.")}
        </Typography>

        <Typography variant="bodyMedium" align="center" color="textSecondary">
          {t("learning.result.titles.youHad", "You had")} {item.correctAnswersCount}{" "}
          {t("learning.result.titles.correct", "answers")} {t("learning.result.titles.of", "of")}{" "}
          {item.totalQuestionsCount} {t("learning.result.titles.possible", "possible")}.{" "}
          {t("learning.result.titles.approvedLimit", "The limit for approved is")} {item.requiredPassingScore}
          .
        </Typography>
      </Grid>
    </Grid>
  )
}

const StyledPaperBox = styled(PaperBox)(({ theme }) => ({
  [theme.breakpoints.up("md")]: {
    marginBottom: 50
  }
}))

export const ExamFinishedSummary = ({ item }) => {
  const { t } = useTranslation()
  const courses = t("learning.details.course", { returnObjects: true })

  let courseDetail = null
  if (item && item.examinationType) {
    courseDetail = courses.find((course) => course.examinationType === item.examinationType)
  }

  return (
    <Container>
      <Grid container spacing={6}>
        <Grid item xs={12} md={8}>
          <Typography variant="h6" color={theme.palette.primary[700]}>
            {t("learning.titles.course", "Course")}
          </Typography>
          <Typography variant="h3" marginBottom={3}>
            {courseDetail ? courseDetail?.examinationName : item.examinationName}
          </Typography>
          <StyledPaperBox>
            {item.calculatedPassed ? <PassedExam item={item} /> : <NotPassedExam item={item} />}
            <Box marginTop={3}>
              <TodoListWrapper>
                {item.areas
                  .filter((x) => x.totalCorrectCount > 0)
                  .map((data, index) => {
                    return (
                      <TodoListRow
                        id={index}
                        control={
                          data.allCorrect ? (
                            <TodoListCheckbox color="success" checked disabled />
                          ) : (
                            <TodoListCheckbox color="error" icon={faTimesCircle} disabled />
                          )
                        }>
                        <Grid container direction="row" justifyContent="space-between">
                          <Grid item>
                            {courseDetail
                              ? courseDetail.examinationDefinition?.areas[index].title
                              : data.title}
                          </Grid>
                          <Grid item>
                            <Typography
                              color={data.allCorrect ? "textSecondary" : theme.palette.error["main"]}>
                              ({data.totalCorrectAnswersCount} / {data.totalCorrectCount}{" "}
                              {t("learning.result.titles.correct", "answers")})
                            </Typography>
                          </Grid>
                        </Grid>
                      </TodoListRow>
                    )
                  })}
              </TodoListWrapper>
            </Box>
            <Grid container direction="row" justifyContent="space-between" marginTop={3}>
              <Grid item>
                <Typography variant="h3" marginBottom={3}>
                  {t("learning.result.titles.yourResult", "Your result")}
                </Typography>
              </Grid>
              <Grid item>
                <Typography
                  variant="h3"
                  marginBottom={3}
                  color={item.calculatedPassed ? theme.palette.success["main"] : theme.palette.error["main"]}>
                  {item.correctAnswersCount} {t("learning.result.titles.of", "of")} {item.totalQuestionsCount}{" "}
                  {t("learning.result.titles.correct", "answers")}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              spacing={2}
              container
              direction="column"
              justifyContent="center"
              alignItems="center"
              marginY={3}>
              <Grid item>
                {item.calculatedPassed ? (
                  <Button component={NavLink} to={allRoutes.LearningUser} variant="contained">
                    {t("learning.result.titles.downloadCert", "Download certificate")}
                  </Button>
                ) : (
                  <Button
                    component={NavLink}
                    to={allRoutes.LearningUserExamsExamination(item.userExaminationId)}
                    variant="contained">
                    {t("learning.result.buttons.redoTest", "Redo the test")}
                  </Button>
                )}
              </Grid>
            </Grid>
          </StyledPaperBox>
        </Grid>
      </Grid>
    </Container>
  )
}
